import { useMemo, useState } from 'react';
import { ExpenseType } from 'api/model';
import type { ExpenseTypeThresholdDto, UpdateExpenseTypeThresholdModel } from 'api/model';
import {
  useGlobalSettingsGetExpenseTypeThresholds,
  useGlobalSettingsUpdateExpenseTypeThreshold,
} from 'api/global-settings/global-settings';
import useTranslations from 'contexts/basicData/useTranslations';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';
import styled from 'styled-components';
import { faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showException } from 'utils/exception-helper';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CellRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface ThresholdRow {
  expenseType: ExpenseType;
  name: string;
  manualUploadThreshold: number;
  automaticUploadThreshold: number;
}

const allExpenseTypes: ExpenseType[] = Object.values(ExpenseType).filter(
  (v): v is ExpenseType => typeof v === 'number' && v !== ExpenseType.Gas
);

const ExpenseTypeThresholdSettings = () => {
  const me = useMe();
  const isSuperAdmin = includesRoles([Roles.SuperAdmin], me?.roles);
  const { expenseTypes: expenseTypeTranslations } = useTranslations();
  const { data: thresholds, isLoading, refetch } =
    useGlobalSettingsGetExpenseTypeThresholds();
  const updateCall = useGlobalSettingsUpdateExpenseTypeThreshold();

  // Local edits that differ from server state, keyed by `${expenseType}-${field}`
  const [localEdits, setLocalEdits] = useState<Record<string, number>>({});
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());

  const getThreshold = (expenseType: ExpenseType): ExpenseTypeThresholdDto | undefined =>
    thresholds?.find((t) => t.expenseType === expenseType);

  const rows = useMemo((): ThresholdRow[] => {
    return allExpenseTypes.map((et) => {
      const threshold = getThreshold(et);
      return {
        expenseType: et,
        name:
          (expenseTypeTranslations as Record<string, string>)?.[String(et)] ??
          `Typ ${et}`,
        manualUploadThreshold: threshold?.manualUploadThreshold ?? 1000,
        automaticUploadThreshold: threshold?.automaticUploadThreshold ?? 1000,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds, expenseTypeTranslations]);

  const getDisplayValue = (row: ThresholdRow, field: 'manualUploadThreshold' | 'automaticUploadThreshold') => {
    const key = `${row.expenseType}-${field}`;
    return localEdits[key] ?? row[field];
  };

  const hasRowChanged = (row: ThresholdRow) => {
    const manualKey = `${row.expenseType}-manualUploadThreshold`;
    const autoKey = `${row.expenseType}-automaticUploadThreshold`;
    return manualKey in localEdits || autoKey in localEdits;
  };

  const handleChange = (
    expenseType: ExpenseType,
    field: 'manualUploadThreshold' | 'automaticUploadThreshold',
    value: string
  ) => {
    const numValue = parseInt(value, 10);
    if (Number.isNaN(numValue) || numValue < 0) return;

    const serverRow = rows.find((r) => r.expenseType === expenseType);
    const key = `${expenseType}-${field}`;

    if (serverRow && numValue === serverRow[field]) {
      // Value matches server — remove from local edits
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setLocalEdits((prev) => ({ ...prev, [key]: numValue }));
    }
  };

  const handleSave = async (row: ThresholdRow) => {
    const updatedThreshold: UpdateExpenseTypeThresholdModel = {
      expenseType: row.expenseType,
      manualUploadThreshold: getDisplayValue(row, 'manualUploadThreshold'),
      automaticUploadThreshold: getDisplayValue(row, 'automaticUploadThreshold'),
    };

    setSavingRows((prev) => new Set(prev).add(row.expenseType));
    try {
      await updateCall.mutateAsync({ data: updatedThreshold });
      // Clear local edits for this row
      setLocalEdits((prev) => {
        const next = { ...prev };
        delete next[`${row.expenseType}-manualUploadThreshold`];
        delete next[`${row.expenseType}-automaticUploadThreshold`];
        return next;
      });
      refetch();
    } catch (error) {
      showException(error);
    } finally {
      setSavingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.expenseType);
        return next;
      });
    }
  };

  const columnSettings = useMemo(
    (): ColumnSetting<ThresholdRow, object>[] => [
      {
        head: 'Utläggstyp',
        width: 200,
        render: (row) => <span>{row.name}</span>,
        sortFunction: (a, b) => a.name.localeCompare(b.name),
      },
      {
        head: 'Manuell uppladdning (kr)',
        width: 200,
        render: (row) => (
          <Input
            type="number"
            min={0}
            small
            width={100}
            disabled={!isSuperAdmin}
            aria-label={`Manuell gräns för ${row.name}`}
            value={getDisplayValue(row, 'manualUploadThreshold')}
            onChange={(e) =>
              handleChange(
                row.expenseType,
                'manualUploadThreshold',
                e.target.value
              )
            }
          />
        ),
      },
      {
        head: 'Automatisk uppladdning (kr)',
        width: 200,
        render: (row) => (
          <Input
            type="number"
            min={0}
            small
            width={100}
            disabled={!isSuperAdmin}
            aria-label={`Automatisk gräns för ${row.name}`}
            value={getDisplayValue(row, 'automaticUploadThreshold')}
            onChange={(e) =>
              handleChange(
                row.expenseType,
                'automaticUploadThreshold',
                e.target.value
              )
            }
          />
        ),
      },
      ...(isSuperAdmin ? [{
        head: '',
        width: 50,
        render: (row: ThresholdRow) => (
          <CellRow>
            {savingRows.has(row.expenseType) ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : hasRowChanged(row) ? (
              <TextButton onClick={() => handleSave(row)}>
                <FontAwesomeIcon icon={faSave} />
              </TextButton>
            ) : null}
          </CellRow>
        ),
      }] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, localEdits, savingRows, isSuperAdmin]
  );

  if (isLoading) {
    return <FontAwesomeIcon icon={faSpinner} spin />;
  }

  return (
    <Wrapper>
      <h1>
        Tröskelvärden per utläggstyp (autogodkännande){' '}
        {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
      </h1>
      <Table fluid columnSettings={columnSettings} rows={rows} />
    </Wrapper>
  );
};

export default ExpenseTypeThresholdSettings;
